import React, { useEffect } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { Button, Grid, IconButton, Alert } from '@mui/material'
import { StateMethods, useHookstate } from '@hookstate/core'
import CloseIcon from '@mui/icons-material/Close'
import { useLang } from '~/hooks/useLang'
import FilterAutocompleteWarehouse from '~/components/FilterAutocompleteWarehouse'
import { transferEquipment } from '../../services'
import notify from '~/utils/notify'

export interface TransferDialogProps {
  readonly open: StateMethods<boolean>
  readonly equipmentId: number | null
  readonly equipmentName: string | null
  readonly isAvailable: boolean
  readonly handleClose: () => void
  readonly onTransferSuccess: () => void
}

export default function TransferDialog({
  open,
  equipmentId,
  equipmentName,
  isAvailable,
  handleClose,
  onTransferSuccess,
}: TransferDialogProps) {
  const { translate } = useLang()
  const warehouseId = useHookstate<number | null>(null)
  const loading = useHookstate<boolean>(false)

  useEffect(() => {
    if (!open.get()) {
      warehouseId.set(null)
    }
  }, [open.get()])

  const handleTransfer = async () => {
    if (!warehouseId.get()) {
      notify({ message: translate('Please select a warehouse'), type: 'error' })
      return
    }

    if (!equipmentId) {
      notify({ message: translate('Equipment ID not found'), type: 'error' })
      return
    }

    loading.set(true)
    try {
      await transferEquipment(equipmentId, warehouseId.get()!)
      notify({
        message: translate('Equipment successfully transferred!'),
        type: 'success',
      })
      handleClose()
      onTransferSuccess()
    } finally {
      loading.set(false)
    }
  }

  const clickOutModalClose = (_event: React.SyntheticEvent, reason: string) => {
    if (reason !== 'backdropClick') {
      open.set(false)
    }
  }

  return (
    <Dialog
      fullWidth
      maxWidth={'sm'}
      open={open.get()}
      onClose={clickOutModalClose}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        {translate('Transfer Equipment')}
        <IconButton aria-label="close" onClick={() => handleClose()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 2 }}>
          {!isAvailable && (
            <Grid item xs={12}>
              <Alert severity="error">
                {translate(
                  'Este equipamento foi cautelado por um usuário e não é possível realizar sua transferência até devolução.'
                )}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <FilterAutocompleteWarehouse
              onWarehouseChange={(value) => warehouseId.set(value)}
              warehouseId={warehouseId.get()}
              disabled={!isAvailable || loading.get()}
              isRequired={true}
            />
          </Grid>

          <Grid item xs={12} display={'flex'} justifyContent={'flex-end'} gap={2}>
            <Button variant="outlined" onClick={() => handleClose()} disabled={loading.get()}>
              {translate('Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleTransfer}
              disabled={!isAvailable || loading.get()}
            >
              {translate('Transfer')}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
